/**
 * MessageActions — standardized feedback widget (Feedback & Correction Module).
 *
 * Used by every chat surface with a tool_run_id:
 *   • thumbs (👍/👎) open a small QUICK-PICK MODAL when reasons exist:
 *       one-tap reason CHIP = vote + reason submitted; or dismiss
 *     (no reasons → the thumb votes directly)
 *   • optional free-text: inside the modal, plus "＋ Add a note" after voting
 *   • state machine: idle → voted → reprocessing → updated
 *   • polls /api/feedback/versions while a reprocess is in flight; the old
 *     content stays visible until a new is_current version lands.
 */
import React, { useEffect, useRef, useState } from 'react';
import { ThumbsUp, ThumbsDown, Loader2, X } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

export type FeedbackReason = { code: string; label: string };
export type FeedbackAction = 'reprocess' | 'admin_review' | null;

export interface MessageActionsProps {
  toolRunId: string;
  contentKey?: string;
  reasonsDown?: FeedbackReason[];
  reasonsUp?: FeedbackReason[];
  initialRating?: 'up' | 'down' | null;
  onFeedback: (input: {
    rating: 'up' | 'down';
    reasonCode?: string;
    comment?: string;
  }) => Promise<{ actionTriggered: FeedbackAction }>;
}

type UiState = 'idle' | 'voted' | 'reprocessing' | 'updated';

interface ContentVersion {
  is_current: boolean;
  created_at: string;
}

interface VersionsResponse {
  success: boolean;
  data?: ContentVersion[];
  regenerate_status?: 'pending' | 'running' | 'done' | 'failed' | null;
}

const POLL_ATTEMPTS_MAX = 30; // ~2 min of polling, then settle to voted

export const MessageActions: React.FC<MessageActionsProps> = ({
  toolRunId,
  contentKey,
  reasonsDown,
  reasonsUp,
  initialRating = null,
  onFeedback,
}) => {
  const [rating, setRating] = useState<'up' | 'down' | null>(initialRating);
  const [state, setState] = useState<UiState>('idle');
  const [regenStatus, setRegenStatus] = useState<
    'pending' | 'running' | 'done' | 'failed' | null
  >(null);
  const [modal, setModal] = useState<'up' | 'down' | null>(null);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | null>(null);

  // Respect an externally-provided vote (e.g. restored from /feedback/mine).
  useEffect(() => {
    if (initialRating && !rating) setRating(initialRating);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRating]);

  // Poll versions while a reprocess is in flight — never destructive, the
  // old content stays up until the new current version arrives. Bounded: if
  // no worker picks the job up (e.g. lanes paused), settle back to 'voted'
  // instead of showing "Reprocessing…" forever.
  useEffect(() => {
    if (state !== 'reprocessing') return;

    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await apiFetch(
          `/api/feedback/versions?tool_run_id=${toolRunId}${
            contentKey ? `&content_key=${encodeURIComponent(contentKey)}` : ''
          }`
        );
        const d = (await res.json()) as VersionsResponse;
        const current = (d?.data ?? []).find((v) => v.is_current);
        if (current) {
          setState('updated');
          if (timer.current) window.clearInterval(timer.current);
          return;
        }
        setRegenStatus(d.regenerate_status ?? null);
        if (attempts >= POLL_ATTEMPTS_MAX) {
          // Job still queued/never completing — show a settled vote state.
          setState('voted');
          if (timer.current) window.clearInterval(timer.current);
        }
      } catch {
        if (attempts >= POLL_ATTEMPTS_MAX) {
          setState('voted');
          if (timer.current) window.clearInterval(timer.current);
        }
      }
    };

    void poll();
    timer.current = window.setInterval(poll, 4000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [state, toolRunId, contentKey]);

  const submit = async (
    side: 'up' | 'down',
    reasonCode?: string,
    comment?: string
  ) => {
    setBusy(true);
    try {
      const { actionTriggered } = await onFeedback({
        rating: side,
        reasonCode,
        comment,
      });
      setRating(side);
      setModal(null);
      setNote('');
      setNoteOpen(false);
      if (actionTriggered === 'reprocess') {
        setRegenStatus('pending');
        setState('reprocessing');
      } else setState('voted');
    } catch {
      setState('voted');
    } finally {
      setBusy(false);
    }
  };

  /** Thumbs: no reasons → vote directly; reasons → quick-pick modal. */
  const onThumb = (side: 'up' | 'down') => {
    if (busy || state === 'reprocessing') return;
    const reasons = side === 'down' ? (reasonsDown ?? []) : (reasonsUp ?? []);
    if (reasons.length === 0) {
      void submit(side);
      return;
    }
    setModal(side);
    setNote('');
  };

  const btnCls =
    'inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors';

  const modalReasons = modal
    ? modal === 'down'
      ? (reasonsDown ?? [])
      : (reasonsUp ?? [])
    : [];

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1">
      <button
        type="button"
        disabled={busy || state === 'reprocessing'}
        onClick={() => onThumb('up')}
        className={`${btnCls} ${
          rating === 'up'
            ? 'bg-green-100 text-green-800'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Good feedback"
      >
        <ThumbsUp className="h-3.5 w-3.5" />
        {rating === 'up' ? 'Helpful' : ''}
      </button>
      <button
        type="button"
        disabled={busy || state === 'reprocessing'}
        onClick={() => onThumb('down')}
        className={`${btnCls} ${
          rating === 'down'
            ? 'bg-red-100 text-red-800'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Bad feedback"
      >
        <ThumbsDown className="h-3.5 w-3.5" />
        {rating === 'down' ? 'Not helpful' : ''}
      </button>

      {state === 'reprocessing' && (
        <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
          <Loader2 className="h-3 w-3 animate-spin" />
          {regenStatus === 'running'
            ? 'Reprocessing…'
            : regenStatus === 'done' || regenStatus === 'failed'
              ? 'Update pending…'
              : 'Queued for improvement…'}
        </span>
      )}
      {state === 'updated' && (
        <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-2 py-1 text-[11px] font-medium text-blue-700">
          Updated
        </span>
      )}

      {rating && state !== 'reprocessing' && state !== 'updated' && (
        <>
          {!noteOpen ? (
            <button
              type="button"
              onClick={() => setNoteOpen(true)}
              className="rounded-md px-1.5 py-0.5 text-[11px] font-medium text-gray-400 hover:text-gray-600"
            >
              ＋ Add a note
            </button>
          ) : (
            <span className="inline-flex items-center gap-1">
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && note.trim()) {
                    e.preventDefault();
                    void submit(rating, undefined, note.trim());
                  }
                }}
                placeholder="Add a comment (optional)"
                className="h-7 w-44 rounded-md border border-gray-300 px-2 text-xs"
                autoFocus
              />
              <button
                type="button"
                disabled={busy || !note.trim()}
                onClick={() => void submit(rating, undefined, note.trim())}
                className="rounded-md bg-gray-900 px-2 py-1 text-[11px] font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
              >
                {busy ? '…' : 'Note'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNoteOpen(false);
                  setNote('');
                }}
                className="text-[11px] text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </span>
          )}
        </>
      )}

      {/* ── Quick-pick modal: chip = vote + reason in one tap; dismiss = X ── */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
          onClick={() => setModal(null)}
        >
          <div
            className="w-full max-w-xs rounded-2xl bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-bold text-gray-900">
                  {modal === 'down'
                    ? 'What was wrong with this response?'
                    : 'What was helpful?'}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pick one, or add a note — it helps us improve.
                </p>
              </div>
              <button
                type="button"
                aria-label="Dismiss"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setModal(null)}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-3 flex flex-col gap-1.5">
              {modalReasons.map((r) => (
                <button
                  key={r.code}
                  type="button"
                  disabled={busy}
                  onClick={() => void submit(modal, r.code)}
                  className={`rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors ${
                    modal === 'down'
                      ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100'
                      : 'border-green-200 bg-green-50 text-green-800 hover:bg-green-100'
                  }`}
                >
                  {r.label}
                </button>
              ))}

              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && note.trim()) {
                    e.preventDefault();
                    void submit(modal, undefined, note.trim());
                  }
                }}
                placeholder="Or add a comment (optional)"
                className="h-8 rounded-lg border border-gray-300 px-2 text-xs"
                autoFocus
              />
              {note.trim() && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void submit(modal, undefined, note.trim())}
                  className="rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-700 disabled:opacity-40"
                >
                  {busy ? 'Sending…' : 'Submit'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
